import { Navigate } from "react-router-dom";
import { useGetOrganisationFeatureFlag } from "@tether-web-portal/api/organisations/hooks";
import { Loader } from "../loading/Loader";

interface HiddenWhenFlaggedProps {
  /** An organisation feature flag whose TRUE value hides the route. */
  flag: string;
  /** Where to send someone who reaches the route anyway. */
  redirectTo?: string;
  children: JSX.Element;
}

/**
 * A route that an organisation feature flag can switch OFF.
 *
 * Note the polarity: the flag names what to HIDE, so a missing flag — which is every
 * organisation until someone sets one — leaves the route exactly as it is today. That is
 * the safe default for a switch whose whole job is to take a page away.
 *
 * The loading branch is not cosmetic. The flags arrive from `GET
 * /v2/organisationfeatureflag` a moment after the route mounts, and
 * `useGetOrganisationFeatureFlag` reports `false` until they do — so rendering the
 * children while the answer is in flight paints the hidden page for a beat before
 * removing it, which is worse than not hiding it at all. Redirecting while it is in
 * flight has the opposite failure and is worse still: it bounces people out of a page
 * they are allowed to see.
 */
export const HiddenWhenFlagged = ({ flag, redirectTo = "/", children }: HiddenWhenFlaggedProps) => {
  const { value: isHidden, isLoading } = useGetOrganisationFeatureFlag(flag);

  if (isLoading) {
    return <Loader />;
  }

  if (isHidden) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};
